import { useMutation } from '@ngtrvu/use-api';
import { useQueryClient } from '@tanstack/react-query';
import { Form, message } from 'antd';
import MetaDecorator from 'apps/home/components/MetaDecorator';
import PageTitle from 'apps/home/components/PageTitle';
import {
  IndustryApiCall,
  SectorApiCall,
  SubSectorApiCall,
  SuperSectorApiCall,
} from 'apps/catalog/apiCalls/stock-icb';
import { DynamicFilterForm } from 'common/dynamic-filter-form';
import { DetailPage, ListPage } from 'common/ui/admin-CRUD/page';
import { useFetchApiGet } from 'hooks/api';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  industryColumnsGenerator,
  sectorColumnsGenerator,
  subSectorColumnsGenerator,
  superSectorColumnsGenerator,
} from './config';
import {
  IndustryDetail,
  SectorDetail,
  SubSectorDetail,
  SuperSectorDetail,
} from './detail';
import {
  ICBEditForm,
  IndustryCreateForm,
  SectorCreateForm,
  SubSectorCreateForm,
  SuperSectorCreateForm,
} from './form';

// ─── Shared edit page factory ─────────────────────────────────────────────────

const makeEditPage = (
  ApiCall: any,
  listUrl: string,
  title: string,
  formName: string
) => () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: item, isLoading, load } = useFetchApiGet(ApiCall.detail, {
    resourceName: 'data',
  });

  useEffect(() => {
    if (id) load({ id });
  }, [id]);

  // @ts-ignore
  const { mutate: update, isPending } = useMutation(ApiCall.edit, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ApiCall.detail.queryKey] });
      queryClient.invalidateQueries({ queryKey: [ApiCall.list.queryKey] });
      message.success(`${title} updated successfully`);
      navigate(listUrl);
    },
    onError: (err: any) => {
      message.error(err?.message || `Failed to update ${title}`);
    },
  });

  const onFinish = (values: any) => {
    update({
      id,
      payload: { name: values.name, name_en: values.name_en ?? '' },
    });
  };

  if (isLoading) return null;

  return (
    <>
      <MetaDecorator title={`Edit ${title}`} />
      <PageTitle title={`Edit ${title}`} backUrl={listUrl} />
      <ICBEditForm
        form={form}
        submiting={isPending}
        onFinish={onFinish}
        initialValues={item ? { name: item.name, name_en: item.name_en } : {}}
        name={formName}
      />
    </>
  );
};

// ─── Shared create page factory ───────────────────────────────────────────────

const makeCreatePage = (
  ApiCall: any,
  listUrl: string,
  title: string,
  CreateFormComponent: any
) => () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // @ts-ignore
  const { mutate: create, isPending } = useMutation(ApiCall.create, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ApiCall.list.queryKey] });
      message.success(`${title} created successfully`);
      navigate(listUrl);
    },
    onError: (err: any) => {
      message.error(err?.message || `Failed to create ${title}`);
    },
  });

  const onFinish = (values: any) => {
    create({ payload: values });
  };

  return (
    <>
      <MetaDecorator title={`Create ${title}`} />
      <PageTitle title={`Create ${title}`} backUrl={listUrl} />
      <CreateFormComponent
        form={form}
        submiting={isPending}
        onFinish={onFinish}
        initialValues={{}}
      />
    </>
  );
};

// ─── Industries (Level 1) ─────────────────────────────────────────────────────

const INDUSTRY_URL = '/catalog/stock-icb/industries';

export const ICBIndustryListPage = () => (
  <ListPage
    title="ICB Industries (Level 1)"
    apiCall={IndustryApiCall}
    columnsGenerator={industryColumnsGenerator}
    FilterForm={DynamicFilterForm}
    createUrl={`${INDUSTRY_URL}/create`}
  />
);

export const ICBIndustryDetailPage = () => (
  <DetailPage
    title="ICB Industry"
    apiCall={IndustryApiCall}
    listUrl={INDUSTRY_URL}
    ItemDetail={IndustryDetail}
    options={{ editEnabled: true, deleteEnabled: false }}
  />
);

export const ICBIndustryEditPage = makeEditPage(
  IndustryApiCall,
  INDUSTRY_URL,
  'ICB Industry',
  'industry-edit'
);

export const ICBIndustryCreatePage = makeCreatePage(
  IndustryApiCall,
  INDUSTRY_URL,
  'ICB Industry',
  IndustryCreateForm
);

// ─── SuperSectors (Level 2) ───────────────────────────────────────────────────

const SUPERSECTOR_URL = '/catalog/stock-icb/supersectors';

export const ICBSuperSectorListPage = () => (
  <ListPage
    title="ICB SuperSectors (Level 2)"
    apiCall={SuperSectorApiCall}
    columnsGenerator={superSectorColumnsGenerator}
    FilterForm={DynamicFilterForm}
    createUrl={`${SUPERSECTOR_URL}/create`}
  />
);

export const ICBSuperSectorDetailPage = () => (
  <DetailPage
    title="ICB SuperSector"
    apiCall={SuperSectorApiCall}
    listUrl={SUPERSECTOR_URL}
    ItemDetail={SuperSectorDetail}
    options={{ editEnabled: true, deleteEnabled: false }}
  />
);

export const ICBSuperSectorEditPage = makeEditPage(
  SuperSectorApiCall,
  SUPERSECTOR_URL,
  'ICB SuperSector',
  'supersector-edit'
);

export const ICBSuperSectorCreatePage = makeCreatePage(
  SuperSectorApiCall,
  SUPERSECTOR_URL,
  'ICB SuperSector',
  SuperSectorCreateForm
);

// ─── Sectors (Level 3) ────────────────────────────────────────────────────────

const SECTOR_URL = '/catalog/stock-icb/sectors';

export const ICBSectorListPage = () => (
  <ListPage
    title="ICB Sectors (Level 3)"
    apiCall={SectorApiCall}
    columnsGenerator={sectorColumnsGenerator}
    FilterForm={DynamicFilterForm}
    createUrl={`${SECTOR_URL}/create`}
  />
);

export const ICBSectorDetailPage = () => (
  <DetailPage
    title="ICB Sector"
    apiCall={SectorApiCall}
    listUrl={SECTOR_URL}
    ItemDetail={SectorDetail}
    options={{ editEnabled: true, deleteEnabled: false }}
  />
);

export const ICBSectorEditPage = makeEditPage(
  SectorApiCall,
  SECTOR_URL,
  'ICB Sector',
  'sector-edit'
);

export const ICBSectorCreatePage = makeCreatePage(
  SectorApiCall,
  SECTOR_URL,
  'ICB Sector',
  SectorCreateForm
);

// ─── SubSectors (Level 4) ─────────────────────────────────────────────────────

const SUBSECTOR_URL = '/catalog/stock-icb/subsectors';

export const ICBSubSectorListPage = () => (
  <ListPage
    title="ICB SubSectors (Level 4)"
    apiCall={SubSectorApiCall}
    columnsGenerator={subSectorColumnsGenerator}
    FilterForm={DynamicFilterForm}
    createUrl={`${SUBSECTOR_URL}/create`}
  />
);

export const ICBSubSectorDetailPage = () => (
  <DetailPage
    title="ICB SubSector"
    apiCall={SubSectorApiCall}
    listUrl={SUBSECTOR_URL}
    ItemDetail={SubSectorDetail}
    options={{ editEnabled: true, deleteEnabled: false }}
  />
);

export const ICBSubSectorEditPage = makeEditPage(
  SubSectorApiCall,
  SUBSECTOR_URL,
  'ICB SubSector',
  'subsector-edit'
);

export const ICBSubSectorCreatePage = makeCreatePage(
  SubSectorApiCall,
  SUBSECTOR_URL,
  'ICB SubSector',
  SubSectorCreateForm
);
